/**
 * dateRangePresets.ts
 *
 * Single source of truth for the legacy date-range presets used by every
 * dashboard / list / CRM filter. Mirrors the arithmetic in
 * html/lib/filters/FiltersDateRange.php::getFilterDateRangeArray() so saved
 * filters keep their relative meaning every time the list is loaded.
 *
 * Preset keys match the legacy keys exactly so any future server-side
 * persistence remains compatible.
 *
 * ORDER IS DISPLAY ORDER, and it is the only thing order affects: every
 * consumer renders this with .map, and a stored filter or report keeps its
 * preset KEY, never an index. The legacy seventeen retain their relative order;
 * 'all' leads because it is the escape hatch for a list that defaults to a
 * range, and the two report presets trail because they were added late.
 */

export type DateRangePresetKey =
  | 'now'
  | 'today'
  | 'yesterday'
  | '365days'
  | 'next90days'
  | 'thisweek'
  | 'lastweek'
  | 'thismonth'
  | 'lastmonth'
  | 'thisyear'
  | 'lastyear'
  | '7days'
  | '14days'
  | '21days'
  | '30days'
  | '60days'
  | '90days'
  // Added for scheduled standard reports. Key strings match
  // FilterNormalizer::resolveDatePreset() on the server exactly — a saved
  // order stores the KEY, and the server is what re-resolves it on every fire.
  | 'lastquarter'
  | 'monthtodate'
  // An explicitly UNBOUNDED range. Resolves to two empty strings, which
  // buildFiltersPayload() omits from the wire, so no date predicate is sent.
  | 'all';

export interface ResolvedDateRange {
  from: string;
  to: string;
}

export interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
  compute: (now: Date) => ResolvedDateRange;
}

// ─── Date helpers ────────────────────────────────────────────────────────────

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + days);
  return next;
}

function format(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function range(from: Date, to: Date): ResolvedDateRange {
  return { from: format(from), to: format(to) };
}

// PHP's strtotime("last Sunday") returns the most recent Sunday strictly
// before today (if today IS Sunday, it returns the prior week's Sunday).
// We replicate that by walking back at least one day, then to the closest
// Sunday (weekday 0).
function lastWeekday(today: Date, weekday: number): Date {
  let cursor = addDays(today, -1);
  while (cursor.getDay() !== weekday) {
    cursor = addDays(cursor, -1);
  }
  return cursor;
}

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function lastOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

// First day of the calendar quarter containing `d`. Month is 0-indexed, so
// Jan–Mar → 0, Apr–Jun → 3, and so on.
function firstOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

// ─── Presets (legacy order) ──────────────────────────────────────────────────

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  // ─── Explicitly unbounded, and FIRST on purpose ───────────────────────────
  // "No Preset Selected" with empty dates ALSO sends no date filter, so on most
  // lists this is a clearer label for something already reachable. It earns its
  // place on a list that DEFAULTS to a range: Payments opens on today (see
  // `defaultPreset` on its daterange config), and there "no preset" falls back
  // to that default — so without this there is no way to ask for the whole
  // history at all. That makes it the widest, most-reached-for option on the
  // one list that needs it, which is why it sits at the top rather than
  // appended after 'monthtodate' like the report presets below.
  //
  // Position is display-only. Presets persist by KEY, never by index, and no
  // consumer indexes into this array — all four render it with .map — so
  // putting this first shifts what users see and nothing else.
  //
  // Server-safe with no PHP change: FilterNormalizer::resolveDatePreset()
  // leaves $dates untouched for a key it does not recognise, and untouched here
  // means the empty range this resolves to — which is exactly right.
  {
    key: 'all',
    label: 'All Dates',
    compute: () => ({ from: '', to: '' }),
  },
  {
    key: 'now',
    label: 'Now',
    compute: (now) => {
      const t = startOfDay(now);
      return range(t, t);
    },
  },
  {
    key: 'today',
    label: 'Today',
    compute: (now) => {
      const t = startOfDay(now);
      return range(t, t);
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    compute: (now) => {
      const y = addDays(startOfDay(now), -1);
      return range(y, y);
    },
  },
  {
    key: '365days',
    label: '365 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -364), t);
    },
  },
  {
    key: 'next90days',
    label: 'Next 90 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(t, addDays(t, 89));
    },
  },
  {
    key: 'thisweek',
    label: 'This Week',
    compute: (now) => {
      const t = startOfDay(now);
      return range(lastWeekday(t, 0), t);
    },
  },
  {
    key: 'lastweek',
    label: 'Last Week',
    compute: (now) => {
      const t = startOfDay(now);
      const lastSat = lastWeekday(t, 6);
      const sun = addDays(lastSat, -6);
      return range(sun, lastSat);
    },
  },
  {
    key: 'thismonth',
    label: 'This Month',
    compute: (now) => range(firstOfMonth(now), lastOfMonth(now)),
  },
  {
    key: 'lastmonth',
    label: 'Last Month',
    compute: (now) => {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return range(firstOfMonth(lastMonth), lastOfMonth(lastMonth));
    },
  },
  {
    key: 'thisyear',
    label: 'This Year-to-date',
    compute: (now) => {
      const t = startOfDay(now);
      return range(new Date(t.getFullYear(), 0, 1), t);
    },
  },
  {
    key: 'lastyear',
    label: 'Last Year',
    compute: (now) => {
      const ly = now.getFullYear() - 1;
      return range(new Date(ly, 0, 1), new Date(ly, 11, 31));
    },
  },
  {
    key: '7days',
    label: '7 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -6), t);
    },
  },
  {
    key: '14days',
    label: '14 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -13), t);
    },
  },
  {
    key: '21days',
    label: '21 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -20), t);
    },
  },
  {
    key: '30days',
    label: '30 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -29), t);
    },
  },
  {
    key: '60days',
    label: '60 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -59), t);
    },
  },
  {
    key: '90days',
    label: '90 Days',
    compute: (now) => {
      const t = startOfDay(now);
      return range(addDays(t, -89), t);
    },
  },

  // ─── Added for scheduled standard reports ─────────────────────────────────
  // Appended rather than slotted next to their siblings so no existing option
  // changes position in any dropdown already in use.
  //
  // A recurring report needs the period that just CLOSED — a reconciliation
  // ordered quarterly must cover last quarter in full. `lastmonth` already
  // covered the monthly case; the quarter had no key.
  {
    key: 'lastquarter',
    label: 'Last Quarter',
    compute: (now) => {
      const startOfThisQuarter = firstOfQuarter(startOfDay(now));
      const startOfLastQuarter = new Date(
        startOfThisQuarter.getFullYear(),
        startOfThisQuarter.getMonth() - 3,
        1,
      );
      return range(startOfLastQuarter, addDays(startOfThisQuarter, -1));
    },
  },
  // Distinct from `thismonth`, which runs to the END of the month. That is fine
  // for a filter and wrong for a report header stating the period covered,
  // because it names dates that have not happened yet.
  {
    key: 'monthtodate',
    label: 'Month to Date',
    compute: (now) => {
      const t = startOfDay(now);
      return range(firstOfMonth(t), t);
    },
  },
];

const PRESET_KEYS = new Set<string>(DATE_RANGE_PRESETS.map((p) => p.key));

export function isDateRangePresetKey(v: unknown): v is DateRangePresetKey {
  return typeof v === 'string' && PRESET_KEYS.has(v);
}

export function resolvePreset(key: DateRangePresetKey, now: Date = new Date()): ResolvedDateRange {
  const preset = DATE_RANGE_PRESETS.find((p) => p.key === key);
  if (!preset) return { from: '', to: '' };
  return preset.compute(now);
}
